import type { QueryClient } from '@tanstack/react-query'
import type {
  CreatePlanUpgradeRequestInput,
  ListAdminPlanUpgradeRequestsResult,
  MyPlanUpgradeRequestsResult,
  PlanUpgradeRequest,
  PlanUpgradeRequestStatus,
  UpdateAdminPlanUpgradeRequestInput,
  UpdatePlanUpgradeRequestInput,
} from '@terafile/shared'
import { apiFetch } from './api.js'

/**
 * 有料プランアップグレード申請の API クライアント。
 *
 * - Owner: createPlanUpgradeRequest / listMyPlanUpgradeRequests
 * - Admin: listAdminPlanUpgradeRequests / updateAdminPlanUpgradeRequest
 */

// ----- Owner -----

export const MY_PLAN_UPGRADE_REQUESTS_QUERY_KEY = ['my-plan-upgrade-requests'] as const

export function prefetchMyPlanUpgradeRequests(queryClient: QueryClient) {
  return queryClient.prefetchQuery({
    queryKey: MY_PLAN_UPGRADE_REQUESTS_QUERY_KEY,
    queryFn: listMyPlanUpgradeRequests,
  })
}

export async function createPlanUpgradeRequest(
  input: CreatePlanUpgradeRequestInput,
): Promise<PlanUpgradeRequest> {
  return apiFetch<PlanUpgradeRequest>('/me/plan-upgrade-requests', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function listMyPlanUpgradeRequests(): Promise<MyPlanUpgradeRequestsResult> {
  return apiFetch<MyPlanUpgradeRequestsResult>('/me/plan-upgrade-requests')
}

export async function updatePlanUpgradeRequest(
  id: string,
  input: UpdatePlanUpgradeRequestInput,
): Promise<PlanUpgradeRequest> {
  return apiFetch<PlanUpgradeRequest>(`/me/plan-upgrade-requests/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export async function cancelPlanUpgradeRequest(id: string): Promise<PlanUpgradeRequest> {
  return apiFetch<PlanUpgradeRequest>(`/me/plan-upgrade-requests/${id}/cancel`, {
    method: 'POST',
  })
}

// ----- Admin -----

export type ListAdminPlanUpgradeRequestsParams = {
  status?: PlanUpgradeRequestStatus
  page?: number
  limit?: number
}

export async function listAdminPlanUpgradeRequests(
  params: ListAdminPlanUpgradeRequestsParams = {},
): Promise<ListAdminPlanUpgradeRequestsResult> {
  const usp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) usp.set(k, String(v))
  }
  const qs = usp.toString()
  return apiFetch<ListAdminPlanUpgradeRequestsResult>(
    `/admin/plan-upgrade-requests${qs ? `?${qs}` : ''}`,
  )
}

export async function updateAdminPlanUpgradeRequest(
  id: string,
  input: UpdateAdminPlanUpgradeRequestInput,
): Promise<ListAdminPlanUpgradeRequestsResult['items'][number]> {
  return apiFetch(`/admin/plan-upgrade-requests/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}
